import { useMemo, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { usePortalsEditorCanvas } from 'portals-platform-lib/editorCanvas';
import { useGetLayoutQuery, useListLayoutsQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/reference/be/rpc/PortalLayoutRpc.hooks';
import { BlockView } from '../components/pageBuilder/blockRegistry';
import { LayoutTabs } from '../components/pageBuilder/LayoutTabs';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_LAYOUT_KEY = 'default';
const CANVAS_MAX_WIDTH = '720px';

// Rows are a 3-column grid: flexible margins with the editor's grab handle in
// the left margin, and the page content in a centered middle column so it
// sits identically for visitors and in the editor.
const PAGE_GRID = {
  display: 'grid',
  gridTemplateColumns: `minmax(32px, 1fr) minmax(0, ${CANVAS_MAX_WIDTH}) minmax(32px, 1fr)`,
  columnGap: '8px',
  alignItems: 'start'
};

// The editor's working copy of a block, addressed by its key.

const noop = () => {};
function PageRow({
  children
}) {
  return /*#__PURE__*/_jsx("div", {
    style: PAGE_GRID,
    children: /*#__PURE__*/_jsx("div", {
      style: {
        gridColumn: 2
      },
      children: children
    })
  });
}
function Loading() {
  return /*#__PURE__*/_jsx(PageRow, {
    children: /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      paddingBlock: "700",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    })
  });
}
function EmptyMessage({
  children
}) {
  return /*#__PURE__*/_jsx(PageRow, {
    children: /*#__PURE__*/_jsx(View, {
      paddingBlock: "700",
      color: "text-core-subtle",
      children: children
    })
  });
}

/** The published layouts, as visitors see them. */
function PublishedLayouts() {
  var _data$layout$blocks;
  const {
    data: layoutsData
  } = useListLayoutsQuery({
    variables: {}
  });
  const layouts = useMemo(() => {
    var _layoutsData$layouts;
    return (_layoutsData$layouts = layoutsData === null || layoutsData === void 0 ? void 0 : layoutsData.layouts) !== null && _layoutsData$layouts !== void 0 ? _layoutsData$layouts : [];
  }, [layoutsData]);
  const [chosenKey, setChosenKey] = useState();
  // Until a tab is picked, show the 'default' layout, or else the first one.
  const activeLayoutKey = chosenKey !== null && chosenKey !== void 0 ? chosenKey : layouts.length === 0 || layouts.some(layout => layout.layoutKey === DEFAULT_LAYOUT_KEY) ? DEFAULT_LAYOUT_KEY : layouts[0].layoutKey;
  const {
    data,
    loading
  } = useGetLayoutQuery({
    variables: {
      layoutKey: activeLayoutKey,
      draft: false
    }
  });
  const blocks = (_data$layout$blocks = data === null || data === void 0 ? void 0 : data.layout.blocks) !== null && _data$layout$blocks !== void 0 ? _data$layout$blocks : [];
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PageRow, {
      children: /*#__PURE__*/_jsx(LayoutTabs, {
        layouts: layouts,
        activeLayoutKey: activeLayoutKey,
        onSelect: setChosenKey
      })
    }), loading && !data ? /*#__PURE__*/_jsx(Loading, {}) : blocks.length === 0 ? /*#__PURE__*/_jsx(EmptyMessage, {
      children: "Nothing published here yet."
    }) : blocks.map((block, index) => /*#__PURE__*/_jsx(PageRow, {
      children: /*#__PURE__*/_jsx("div", {
        style: {
          padding: 8
        },
        children: /*#__PURE__*/_jsx(BlockView, {
          block: block,
          editing: false,
          onFieldChange: noop
        })
      })
    }, index))]
  });
}

/**
 * The page editor's canvas: renders the editor's working copy (its layout tabs
 * and the active layout's blocks) and reports selection, hover, drag-reorder,
 * inline edits and layout tab clicks back to it.
 */
function EditorCanvas({
  canvas
}) {
  const {
    snapshot
  } = canvas;
  const [dragKey, setDragKey] = useState(null);
  if (!snapshot) {
    return /*#__PURE__*/_jsx(Loading, {});
  }
  // Field types arrive as plain strings; they're the same values.
  const blocks = snapshot.blocks;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PageRow, {
      children: /*#__PURE__*/_jsx(LayoutTabs
      // The editor's layouts, so unsaved and renamed ones show too.
      , {
        layouts: snapshot.layouts,
        activeLayoutKey: snapshot.layoutKey
        // Switches the editor's layout, which sends the new one back.
        ,
        onSelect: canvas.selectLayout
      })
    }), blocks.length === 0 ? /*#__PURE__*/_jsx(EmptyMessage, {
      children: "This layout is empty. Add a block from the editor panel."
    }) : blocks.map((block, index) => /*#__PURE__*/_jsxs("div", {
      style: PAGE_GRID,
      onDragOver: event => event.preventDefault(),
      onDragEnter: () => {
        if (dragKey !== null && dragKey !== block._key) {
          canvas.move(dragKey, index);
        }
      },
      onDrop: event => {
        event.preventDefault();
        setDragKey(null);
      },
      children: [/*#__PURE__*/_jsx("div", {
        style: {
          gridColumn: 1,
          justifySelf: 'end',
          paddingTop: 8
        },
        children: /*#__PURE__*/_jsx("span", {
          "aria-hidden": "true",
          draggable: true,
          title: "Drag to reorder",
          onDragStart: () => setDragKey(block._key),
          onDragEnd: () => setDragKey(null),
          style: {
            cursor: 'grab',
            color: '#7c98b6',
            fontSize: 16,
            lineHeight: 1,
            letterSpacing: '-2px',
            userSelect: 'none'
          },
          children: "\u22EE\u22EE"
        })
      }), /*#__PURE__*/_jsx("div", {
        onClick: () => canvas.select(block._key),
        onMouseEnter: () => canvas.hover(block._key),
        onMouseLeave: () => canvas.hover(null),
        style: {
          gridColumn: 2,
          cursor: 'pointer',
          borderRadius: 8,
          padding: 8,
          outline: block._key === snapshot.selectedKey ? '2px solid #7fd1ff' : block._key === snapshot.hoveredKey ? '2px solid #b3e2ff' : '2px solid transparent',
          opacity: block._key === dragKey ? 0.4 : 1
        },
        children: /*#__PURE__*/_jsx(BlockView, {
          block: block,
          editing: true,
          onFieldChange: (name, type, value) => canvas.setField(block._key, name, String(type), value)
        })
      })]
    }, block._key))]
  });
}

/**
 * The portal's layouts page. Visitors see the published layouts; inside the
 * page editor (portals-platform-editor-ui) it's the editor's canvas, which
 * renders only what the editor sends, and all editing controls live in the
 * editor.
 */
export function PageBuilder() {
  const canvas = usePortalsEditorCanvas();
  return /*#__PURE__*/_jsx(View, {
    as: "main",
    minBlockSize: "100%",
    paddingBlock: "700",
    paddingInline: "600",
    backgroundColor: "fill-surface-recessed",
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      children: canvas ? /*#__PURE__*/_jsx(EditorCanvas, {
        canvas: canvas
      }) : /*#__PURE__*/_jsx(PublishedLayouts, {})
    })
  });
}